import type { CellContext, ColumnDef, GroupColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import type { TitleSize } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { differenceInDays, format } from 'date-fns';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import type { ItemV2 } from '@/common/api/types/items';
import type { IVisualizationHeaderProps } from '@/common/api/types/loopVisualization';
import type { SchedulingValidation } from '@/common/api/types/planner/timelineItem';
import type { LabelWithField } from '@/common/components/ui/swimlane/types';
import { formatDayMonth } from '@/common/utils/dateFormats';
import type { DescendantNeedingAttention } from '@/features/planner/components/common/scenario-table/DeadlineRiskBadge';
import { DEFAULT_BASE_COLOR } from '@/features/visualizations/utils/colors';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { TimelineItemCell } from './TimelineItemCell';
import {
  EmptyRowCell,
  ExpandCollapseAllButton,
  MonthHeader,
  ParentRowHeader,
  QuarterHeader,
  RowHeader,
  VerticalParentRowHeader,
  WeekHeader,
} from './TimelineTableHeaderComponents';
import { PARENT_HEADER_HEIGHT, VERTICAL_ROW_HEADER_WIDTH } from './useTimelineColumnDayWidth';
import { buildTimelineDateAxis, getMonthName } from './utils/timelineDateUtils';

export type TimelineItemType = ItemV2 & {
  order?: number;
  progress?: number;
  children?: TimelineItemType[];
  hasChildren?: boolean;
  isLoadingChildren?: boolean;
  isPlaceholder?: boolean;
  schedulingValidations?: SchedulingValidation[] | null;
  isDeletedInItemStore?: boolean;
  hasDeletedDescendant?: boolean;
  latestFinishDateTime?: string | null;
  earliestAncestorDeadlineDateTime?: string | null;
  earliestAncestorDeadlineSourceItemName?: string | null;
  topDescendantsNeedingAttention?: readonly DescendantNeedingAttention[];
  descendantsNeedingAttentionCount?: number;
};

export interface TimelineScenarioContext {
  loopId: number;
  scenarioId: number;
}

export type TimelineRowType = IVisualizationHeaderProps & {
  items?: { [key: string]: TimelineItemType[] };
  allItems?: TimelineItemType[];
  maxOrder?: number;
  subRows?: TimelineRowType[];
  mainHeaderLabel?: string;
  mainHeaderDisplayValues?: IVisualizationHeaderProps['displayValues'];
  mainHeaderColor?: string;
  isFirstSubRow?: boolean;
  firstSubRowId?: string;
  subRowCount?: number;
  totalLanesInGroup?: number;
  longestItemDurationInDays: number;
  parentHeaderSelector?: IVisualizationHeaderProps['selector'];
  headerId: string;
  parentHeaderId?: string;
  calculatedHeight?: number;
};

export type TimelineTableCellContext<TData extends TimelineRowType, TValue> = CellContext<
  TData,
  TValue
> & {
  isRowDraggedOver: boolean;
  isDropForbidden: boolean;
  extraLanes: number;
};

export const columnHelper = createColumnHelper<TimelineRowType>();

export const useTimelineTableColumns = ({
  from,
  to,
  canEdit,
  canResize,
  canDragBetweenHeaders,
  canDragDates,
  isPublishedMode,
  isExportMode = false,
  dayWidth,
  isVertical,
  hasSubHeaders,
  hasVerticalSubHeaders,
  titleSize,
  expandedChildren,
  showChildItems,
  setExpandedChildren,
  onItemClick,
  itemColorById,
  itemLabelsById,
  onResizeEnd,
  isCompact,
  rowHeaderWidth,
  setReorderHeadersModalState,
  onHideHeader,
  progressFieldId,
  systemProgressMap,
  selectedIds,
  previewIds,
  settlingItemIds,
  onSelectionClick,
}: {
  from: string;
  to: string;
  canEdit: boolean;
  canResize: boolean;
  canDragBetweenHeaders: boolean;
  canDragDates: boolean;
  isPublishedMode: boolean;
  isExportMode?: boolean;
  dayWidth: number;
  isVertical: boolean;
  hasSubHeaders: boolean;
  hasVerticalSubHeaders: boolean;
  titleSize: TitleSize;
  expandedChildren: Record<string, boolean>;
  showChildItems: boolean;
  setExpandedChildren: Dispatch<
    SetStateAction<{
      [key: number]: boolean;
    }>
  >;
  setReorderHeadersModalState: React.Dispatch<
    React.SetStateAction<{
      isReorderingMainHeader: boolean;
      parentHeaderId: string | null;
    } | null>
  >;
  onHideHeader?: (headerId: string, isMainHeader: boolean, parentHeaderId?: string) => void;
  onItemClick: (itemId: number) => void;
  itemColorById?: Record<number, string>;
  itemLabelsById?: Map<number, LabelWithField[]>;
  onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
  isCompact?: boolean;
  rowHeaderWidth: number;
  progressFieldId: number | null;
  systemProgressMap: Map<number, number> | null;
  selectedIds?: ReadonlySet<number>;
  previewIds?: ReadonlySet<number> | null;
  settlingItemIds?: ReadonlySet<number>;
  onSelectionClick?: (
    itemId: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
  ) => boolean;
}): ColumnDef<TimelineRowType, { isRowDraggedOver?: boolean }>[] => {
  return useMemo(() => {
    const fixedColumns = [];

    const mainHeaderColumn = columnHelper.accessor('mainHeaderLabel', {
      id: 'main-header',
      header: () => <div className={cn('flex w-full h-full')} />,
      cell: (info) => {
        const row = info.row.original;
        if (!hasVerticalSubHeaders || !row.isFirstSubRow) {
          return null;
        }
        const isExpanded = info.row.getIsExpanded();
        const totalLanesInGroup = row.totalLanesInGroup || 1;
        const marginBuffer = isCompact ? 1 : 4;
        const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
        // When in compact mode, there is no padding/margin between the rows, but in regular mode there is some.
        const totalHeight = !isExpanded
          ? laneHeight - marginBuffer
          : isCompact
            ? totalLanesInGroup * laneHeight + (row.subRowCount || 0) * marginBuffer
            : totalLanesInGroup * laneHeight - marginBuffer;

        const toggleReorderModal =
          !canEdit || isPublishedMode
            ? undefined
            : () => {
                setReorderHeadersModalState({
                  isReorderingMainHeader: true,
                  parentHeaderId: null,
                });
              };

        const hideHeader =
          !canEdit || isPublishedMode || !onHideHeader
            ? undefined
            : () => onHideHeader(row.headerId, true);

        return (
          <VerticalParentRowHeader
            expanded={isExpanded}
            label={info.getValue()}
            displayValues={row.mainHeaderDisplayValues}
            isCompact={isCompact}
            color={row.mainHeaderColor}
            totalHeight={totalHeight}
            onClick={info.row.getToggleExpandedHandler()}
            toggleReorderModal={toggleReorderModal}
            onHideHeader={hideHeader}
          />
        );
      },
      size: hasVerticalSubHeaders ? PARENT_HEADER_HEIGHT : rowHeaderWidth,
    });

    const subHeaderColumn = columnHelper.accessor('label', {
      id: 'row-header',
      header: (info) => {
        if (!hasSubHeaders || isExportMode) {
          return null;
        }
        return (
          <ExpandCollapseAllButton
            areAllExpanded={info.table.getIsAllRowsExpanded()}
            onToggle={info.table.getToggleAllRowsExpandedHandler()}
          />
        );
      },
      cell: (info) => {
        if (hasVerticalSubHeaders) {
          const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
          const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
          if (!isSubGroupExpanded) {
            return null;
          }

          const toggleReorderModal =
            !canEdit || isPublishedMode
              ? undefined
              : () => {
                  setReorderHeadersModalState({
                    isReorderingMainHeader: false,
                    parentHeaderId: info.row.original.parentHeaderId || null,
                  });
                };

          const hideHeader =
            !canEdit || isPublishedMode || !onHideHeader
              ? undefined
              : () =>
                  onHideHeader(info.row.original.headerId, false, info.row.original.parentHeaderId);

          return (
            <RowHeader
              label={info.getValue()}
              displayValues={info.row.original.displayValues}
              color={info.row.original.headerColor}
              isVertical={false}
              isCompact={isCompact}
              toggleReorderModal={toggleReorderModal}
              onHideHeader={hideHeader}
            />
          );
        }
        const isParent = hasSubHeaders && !info.row.original.parentHeaderId;
        const expanded = info.row.getIsExpanded();
        const toggleExpanded = info.row.getToggleExpandedHandler();

        if (isParent) {
          const toggleReorderModal =
            !canEdit || isPublishedMode
              ? undefined
              : () => {
                  setReorderHeadersModalState({
                    isReorderingMainHeader: true,
                    parentHeaderId: null,
                  });
                };
          const hideHeader =
            !canEdit || isPublishedMode || !onHideHeader
              ? undefined
              : () => onHideHeader(info.row.original.headerId, true);
          return (
            <ParentRowHeader
              expanded={expanded}
              label={info.getValue()}
              displayValues={info.row.original.displayValues}
              color={info.row.original.headerColor}
              hasVerticalSubHeaders={hasVerticalSubHeaders}
              onClick={toggleExpanded}
              toggleReorderModal={toggleReorderModal}
              onHideHeader={hideHeader}
            />
          );
        }

        const isChild = !!info.row.depth;
        const isMainHeader = !hasSubHeaders || !info.row.original.parentHeaderId;
        const toggleReorderModal =
          !canEdit || isPublishedMode
            ? undefined
            : () => {
                setReorderHeadersModalState({
                  isReorderingMainHeader: !hasSubHeaders,
                  parentHeaderId: info.row.original.parentHeaderId || null,
                });
              };
        const hideHeader =
          !canEdit || isPublishedMode || !onHideHeader
            ? undefined
            : () =>
                onHideHeader(
                  info.row.original.headerId,
                  isMainHeader,
                  info.row.original.parentHeaderId,
                );
        return (
          <RowHeader
            isChild={isChild}
            label={info.getValue()}
            displayValues={info.row.original.displayValues}
            color={info.row.original.headerColor}
            isVertical={isVertical}
            isCompact={isCompact}
            toggleReorderModal={toggleReorderModal}
            onHideHeader={hideHeader}
          />
        );
      },
      size: isVertical && !hasVerticalSubHeaders ? VERTICAL_ROW_HEADER_WIDTH : rowHeaderWidth,
    });

    if (hasVerticalSubHeaders) {
      fixedColumns.push(mainHeaderColumn, subHeaderColumn);
    } else {
      fixedColumns.push(subHeaderColumn);
    }

    const fixedColumnGroup = columnHelper.group({
      id: 'fixed',
      header: () => null,
      columns: fixedColumns,
    });

    const dateGroupColumns: GroupColumnDef<TimelineRowType, unknown>[] = [];

    const axis = buildTimelineDateAxis({ iterationFrom: from, iterationTo: to });
    if (!axis) return [fixedColumnGroup];

    if (axis.useQuarterView) {
      for (const quarterSlice of axis.quarters) {
        const { year, quarter, quarterLabel, months: monthsToUse } = quarterSlice;

        const monthColumns = monthsToUse.map((month, index) => {
          const accessorKey = `items.${month.key}` as `items.${string}`;
          const monthName = getMonthName(month.start);
          const daysInMonth = differenceInDays(month.end, month.start) + 1;
          const monthWidth = dayWidth * daysInMonth;

          return columnHelper.accessor(accessorKey, {
            id: `${year}-q${quarter}-month-${index}`,
            header: () => <MonthHeader monthName={monthName} isMain={false} />,
            cell: (info) => {
              const { isRowDraggedOver, isDropForbidden, extraLanes, row, getValue } =
                info as TimelineTableCellContext<TimelineRowType, unknown>;
              const original = row.original;
              if (original.subRows?.length) {
                return <EmptyRowCell isCompact={isCompact} headerLeftOffset={0} />;
              }

              if (hasVerticalSubHeaders) {
                const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
                const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
                if (!isSubGroupExpanded) {
                  return <EmptyRowCell isCompact={isCompact} headerLeftOffset={0} isCollapsed />;
                }
              }
              const itemsForMonth = (getValue() || []) as TimelineItemType[];

              const allItemsInRow = row.original.allItems || [];

              return (
                <TimelineItemCell
                  itemsForInterval={itemsForMonth}
                  intervalStart={month.start}
                  from={from}
                  to={to}
                  canDragBetweenHeaders={canDragBetweenHeaders}
                  canDragDates={canDragDates}
                  canResize={canResize}
                  rowColor={
                    row.original.mainHeaderColor || row.original.headerColor || DEFAULT_BASE_COLOR
                  }
                  itemColorById={itemColorById}
                  itemLabelsById={itemLabelsById}
                  alignNameLeft
                  dayWidth={dayWidth}
                  titleSize={titleSize}
                  expandedChildren={expandedChildren}
                  showChildItems={showChildItems}
                  showDependencyDots={false}
                  setExpandedChildren={setExpandedChildren}
                  onResizeEnd={onResizeEnd}
                  row={row.original}
                  isRowDraggedOver={isRowDraggedOver}
                  isDropForbidden={isDropForbidden}
                  isCompact={isCompact}
                  onItemClick={onItemClick}
                  allItemsInRow={allItemsInRow}
                  headerLeftOffset={0}
                  extraLanes={extraLanes}
                  progressFieldId={progressFieldId}
                  systemProgressMap={systemProgressMap}
                  isPublishedMode={isPublishedMode}
                  showDates={false}
                  selectedIds={selectedIds}
                  previewIds={previewIds}
                  settlingItemIds={settlingItemIds}
                  onSelectionClick={onSelectionClick}
                />
              );
            },
            size: monthWidth,
          });
        });

        dateGroupColumns.push(
          columnHelper.group({
            id: `quarter-${year}-${quarter}`,
            header: () => <QuarterHeader quarterLabel={quarterLabel} />,
            columns: monthColumns,
          }),
        );
      }
    } else if (axis.view === 'month') {
      for (const monthGroup of axis.months) {
        const {
          year,
          monthIndex: monthNum,
          monthName,
          visibleMonthStart,
          visibleMonthEnd,
          biWeeks: biWeeksToUse,
          isEmptyState,
        } = monthGroup;
        const daysInMonth = differenceInDays(visibleMonthEnd, visibleMonthStart) + 1;
        const monthWidth = dayWidth * daysInMonth;

        const biWeeksForColumns = isEmptyState
          ? [
              {
                start: visibleMonthStart,
                end: visibleMonthEnd,
                key: format(visibleMonthStart, 'yyyy-MM-dd'),
                index: 0,
                headerLeftOffset: 0,
                biWeekWidth: monthWidth,
                equalColWidth: monthWidth,
              },
            ]
          : biWeeksToUse.map((biWeek) => {
              const equalColWidth = monthWidth / biWeeksToUse.length;
              const daysInBiWeek = differenceInDays(biWeek.end, biWeek.start) + 1;
              const biWeekWidth = dayWidth * daysInBiWeek;
              const offsetFromVisibleStart = differenceInDays(biWeek.start, visibleMonthStart);
              const dateBasedLeftOffset = offsetFromVisibleStart * dayWidth;
              const columnLeftOffset = biWeek.index * equalColWidth;
              const headerLeftOffset = dateBasedLeftOffset - columnLeftOffset;

              return {
                start: biWeek.start,
                end: biWeek.end,
                key: biWeek.key,
                index: biWeek.index,
                headerLeftOffset,
                biWeekWidth,
                equalColWidth,
              };
            });

        const biWeekColumns = biWeeksForColumns.map((biWeek) => {
          const accessorKey = `items.${biWeek.key}` as `items.${string}`;

          return columnHelper.accessor(accessorKey, {
            id: isEmptyState
              ? `${year}-${monthNum}-empty`
              : `${year}-${monthNum}-biweek-${biWeek.index}`,
            header: () =>
              isEmptyState ? null : (
                <WeekHeader
                  rangeStart={formatDayMonth(biWeek.start)}
                  rangeEnd={formatDayMonth(biWeek.end)}
                  leftOffset={biWeek.headerLeftOffset}
                  width={biWeek.biWeekWidth}
                />
              ),
            cell: (info) => {
              const { isRowDraggedOver, isDropForbidden, extraLanes, row, getValue } =
                info as TimelineTableCellContext<TimelineRowType, unknown>;
              const original = row.original;

              if (original.subRows?.length) {
                return (
                  <EmptyRowCell isCompact={isCompact} headerLeftOffset={biWeek.headerLeftOffset} />
                );
              }

              if (hasVerticalSubHeaders) {
                const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
                const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
                if (!isSubGroupExpanded) {
                  return (
                    <EmptyRowCell
                      isCompact={isCompact}
                      headerLeftOffset={biWeek.headerLeftOffset}
                      isCollapsed
                    />
                  );
                }
              }

              const itemsForInterval = isEmptyState
                ? []
                : ((getValue() || []) as TimelineItemType[]);
              const allItemsInRow = row.original.allItems || [];
              return (
                <TimelineItemCell
                  itemsForInterval={itemsForInterval}
                  intervalStart={biWeek.start}
                  from={from}
                  to={to}
                  canResize={canResize}
                  canDragBetweenHeaders={canDragBetweenHeaders}
                  canDragDates={canDragDates}
                  rowColor={
                    row.original.mainHeaderColor || row.original.headerColor || DEFAULT_BASE_COLOR
                  }
                  itemColorById={itemColorById}
                  itemLabelsById={itemLabelsById}
                  alignNameLeft
                  dayWidth={dayWidth}
                  titleSize={titleSize}
                  expandedChildren={expandedChildren}
                  showChildItems={showChildItems}
                  setExpandedChildren={setExpandedChildren}
                  onResizeEnd={onResizeEnd}
                  onItemClick={onItemClick}
                  row={row.original}
                  isRowDraggedOver={isRowDraggedOver}
                  isDropForbidden={isDropForbidden}
                  isCompact={isCompact}
                  allItemsInRow={allItemsInRow}
                  headerLeftOffset={biWeek.headerLeftOffset}
                  extraLanes={extraLanes}
                  progressFieldId={progressFieldId}
                  systemProgressMap={systemProgressMap}
                  isPublishedMode={isPublishedMode}
                  showDependencyDots={false}
                  showDates={false}
                  selectedIds={selectedIds}
                  previewIds={previewIds}
                  settlingItemIds={settlingItemIds}
                  onSelectionClick={onSelectionClick}
                />
              );
            },
            size: biWeek.equalColWidth,
          });
        });

        dateGroupColumns.push(
          columnHelper.group({
            id: `month-${year}-${monthNum}-${monthName}`,
            header: () => <MonthHeader monthName={monthName} isMain />,
            columns: biWeekColumns,
          }),
        );
      }
    }

    return [fixedColumnGroup, ...dateGroupColumns];
  }, [
    from,
    to,
    onResizeEnd,
    onItemClick,
    isVertical,
    hasSubHeaders,
    hasVerticalSubHeaders,
    dayWidth,
    itemColorById,
    itemLabelsById,
    expandedChildren,
    showChildItems,
    setExpandedChildren,
    titleSize,
    isCompact,
    rowHeaderWidth,
    canEdit,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    isPublishedMode,
    isExportMode,
    progressFieldId,
    systemProgressMap,
    setReorderHeadersModalState,
    onHideHeader,
    selectedIds,
    previewIds,
    settlingItemIds,
    onSelectionClick,
  ]);
};
